import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Typography, Button } from '@material-ui/core';
import AppState from '../../../../models/redux/state/app.state';
import { getUserByUrlAPIcall } from '../../../../redux/actions/auth/auth.actions';
import HomeNavBar from '../../../micro/HomeNavBar/HomeNavBar';
import HomeFooter from '../../../micro/HomeFooter/HomeFooter';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import history from '../../../../services/browserHistory';
import { blue } from '../../../../app/theme';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: '100vh',
  },
  columnsContainer: {
    margin: '1.5em 0 6em',
    display: 'flex',
    alignItems: 'stretch',
  },
  contentContainer: {
    alignSelf: 'flex-start',
    marginRight: '92px',
  },
  sideImageContainer: {
    marginRight: '63px',
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    margin: '1rem 0',
    fontSize: '2em',
  },
  subTitle: {
    margin: '1rem 0',
    fontSize: '1.5em',
  },
  interestedText: {
    color: blue,
  },
  list: {
    listStyle: 'none',
    paddingLeft: '1.5em',
    '& > li': {
      fontStyle: 'italic',
      fontWeight: 600,
      paddingBottom: '0.3333em',
    },
    '& > li::before': {
      content: '"\u2022"',
      width: '1em',
      display: 'inline-block',
      color: theme.palette.secondary.main,
      fontWeight: 600,
    },
  },
  trainingButton: {
    height: '112px',
    margin: '20px 10px',
    padding: 0,
    borderRadius: '1em',
    '& *': {
      height: 'inherit',
    },
  },
  sideImage: {
    width: '620px',
  },
  bedLogo: {
    paddingLeft: 65,
    paddingTop: 16,
    width: 239,
    height: 63,
  },
  fullWidthImage: {
    display: 'block',
    width: '100%',
  },
}));

const TrainingListHome = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.auth.user);

  useEffect(() => {
    document.title = 'Blue Earth Diagnostics Available Trainings';
    if (user?.url) {
      dispatch(getUserByUrlAPIcall(user.url));
    }
  
    // Disabling warning because we don't want this effect to run more than once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.url]);

  const handleClickAxumin = () => {
    history.push('/axumin');
  };

  const handleClickPOSLUMA = () => {
    history.push('/posluma');
  };

  return (
    <div className={classes.container}>
      <div>
        <HomeNavBar />
        <img
          className={classes.bedLogo}
          src={`${BASE_URL}${IMAGE_PATHS.BED_LOGO.path}`}
          alt={`${IMAGE_PATHS.BED_LOGO.alt}`}
        />
      </div>
      <div className={classes.columnsContainer}>
        <div className={classes.sideImageContainer}>
          <img
            className={classes.sideImage}
            src={`${BASE_URL}${IMAGE_PATHS.PATIENT_GETTING_SCANNED.path}`}
            alt={`${IMAGE_PATHS.PATIENT_GETTING_SCANNED.alt}`}
          />
        </div>
        <div className={classes.contentContainer}>
          <Typography color="secondary">
            {/* There's no way to satisfy the linter with the following line,
                so simply disable the check */}
            {/* eslint-disable react/jsx-one-expression-per-line */}
            <strong>Welcome back, {user?.firstName}!</strong>
          </Typography>
          <Typography className={classes.title} color="secondary">
            <strong>Blue Earth Diagnostics Training</strong>
          </Typography>
          <Typography className={classes.interestedText} gutterBottom>
            <strong>All training is optimized for desktop use only.</strong>
          </Typography>
          <Typography>
            This platform is designed to offer virtual, on-demand training and patient case studies for:
          </Typography>
          <ul className={classes.list}>
            <li>Nuclear medicine physicians, radiologists, oncologists, and urologists</li>
            <li>Nuclear medicine technologists</li>
            <li>Nuclear medicine professionals and professionals in related fields</li>
          </ul>
          <Typography className={classes.interestedText} gutterBottom>
            <strong>From the below, select the training you are interested in completing.</strong>
          </Typography>
          <Typography className={classes.subTitle} color="secondary" gutterBottom>
            <strong>Available Training:</strong>
          </Typography>
          <Typography gutterBottom>
            <em>As new training becomes available, it will be accessible from this page.</em>
          </Typography>
          <Button onClick={handleClickAxumin} className={classes.trainingButton}>
            <img src={`${BASE_URL}${IMAGE_PATHS.AXUMIN_BUTTON.path}`} alt={`${IMAGE_PATHS.AXUMIN_BUTTON.alt}`} />
          </Button>
          <Button onClick={handleClickPOSLUMA} className={classes.trainingButton}>
            <img src={`${BASE_URL}${IMAGE_PATHS.POSLUMA_BUTTON.path}`} alt={`${IMAGE_PATHS.POSLUMA_BUTTON.alt}`} />
          </Button>
        </div>
      </div>
      <div>
        <img
          className={classes.fullWidthImage}
          src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
          alt={`${IMAGE_PATHS.TRICOLOR_BAR.alt}`}
        />
        <HomeFooter />
      </div>
    </div>
  );
};

export default TrainingListHome;
